<template>
  <div id="manual" class="tab-content active">
    <div class="glass-card p-8 mb-8">
      <h1 class="text-3xl font-bold mb-4 text-gradient">人工降AI服务</h1>
      <p class="text-gray-600 mb-6">我们提供专业的人工降AI服务，由专业文案团队为您的文本进行优化，使其更加自然、人性化。</p>
      
      <div class="grid md:grid-cols-2 gap-8 mb-8">
        <div>
          <h2 class="text-xl font-semibold mb-3 text-green-700">
            <i class="fas fa-user-edit mr-2"></i>专业文案团队
          </h2>
          <p class="text-gray-600">由资深文案专家组成的团队，拥有丰富的写作经验，能够为您提供高质量的文本优化服务。</p>
        </div>
        <div>
          <h2 class="text-xl font-semibold mb-3 text-green-700">
            <i class="fas fa-check-double mr-2"></i>高质量保证
          </h2>
          <p class="text-gray-600">我们保证优化后的文本自然流畅，无AI痕迹，并且保留原文的核心意思和专业性。</p>
        </div>
      </div>
      
      <div class="mb-6">
        <textarea v-model="inputText" class="w-full h-64 p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500" placeholder="请输入需要优化的文本..."></textarea>
      </div>
      
      <div class="flex flex-wrap gap-4">
        <button @click="handleSubmit" class="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-medium transition">
          <i class="fas fa-paper-plane mr-2"></i>提交优化请求
        </button>
      </div>
      
      <div class="mt-8 pt-6 border-t border-gray-200">
        <h3 class="text-xl font-semibold mb-4 text-gray-800">服务流程</h3>
        <div class="flex flex-col md:flex-row gap-6">
          <div class="flex-1 p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
            <div class="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center mb-3">
              <span class="text-green-600 font-bold">1</span>
            </div>
            <h4 class="font-medium mb-2">提交文本</h4>
            <p class="text-gray-600 text-sm">在上方文本框中输入需要优化的内容，点击提交按钮</p>
          </div>
          <div class="flex-1 p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
            <div class="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center mb-3">
              <span class="text-green-600 font-bold">2</span>
            </div>
            <h4 class="font-medium mb-2">专家优化</h4>
            <p class="text-gray-600 text-sm">我们的专业团队会对您的文本进行人工优化，降低AI痕迹</p>
          </div>
          <div class="flex-1 p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
            <div class="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center mb-3">
              <span class="text-green-600 font-bold">3</span>
            </div>
            <h4 class="font-medium mb-2">接收结果</h4>
            <p class="text-gray-600 text-sm">优化完成后，我们会通过邮件发送结果给您</p>
          </div>
        </div>
      </div>
    </div>
    
    <!-- 提交成功模态框 -->
    <div v-if="showSuccessModal" class="modal fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 active">
      <div class="glass-card p-6 max-w-md w-full">
        <div class="flex justify-between items-center mb-4">
          <h3 class="text-xl font-bold text-green-700">
            <i class="fas fa-check-circle mr-2"></i>提交成功
          </h3>
          <button @click="showSuccessModal = false" class="text-gray-500 hover:text-gray-700">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="p-4 bg-green-50 rounded-lg mb-4">
          <p class="text-gray-700">您的优化请求已成功提交，我们的专业团队将尽快处理。</p>
          <p class="text-gray-700 mt-2">优化完成后，我们会通过邮件通知您。</p>
        </div>
        <div class="flex justify-end">
          <button @click="showSuccessModal = false" class="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg">
            确定
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import toast from '../utils/toast'

const inputText = ref('')
const showSuccessModal = ref(false)
// 添加AI处理记录数据
const aiProcessingRecords = ref([])

// 从localStorage加载历史记录
onMounted(() => {
  const savedRecords = localStorage.getItem('aiProcessingRecords')
  if (savedRecords) {
    aiProcessingRecords.value = JSON.parse(savedRecords)
  }
})

// 处理提交
const handleSubmit = async () => {
  if (!inputText.value.trim()) {
    toast.show('请输入需要优化的文本')
    return
  }

  try {
    // 模拟API调用
    await new Promise(resolve => setTimeout(resolve, 1000))

    // 模拟添加处理记录
    const newRecord = {
      timestamp: new Date().toLocaleString(),
      originalText: inputText.value,
      processedText: inputText.value.slice(0, Math.max(10, inputText.value.length * 0.7)),
      originalCharacterCount: inputText.value.length
    }
    aiProcessingRecords.value.unshift(newRecord)

    // 保存到localStorage
    localStorage.setItem('aiProcessingRecords', JSON.stringify(aiProcessingRecords.value))

    // 显示成功模态框
    showSuccessModal.value = true

    // 清空输入框
    inputText.value = ''
  } catch (error) {
    console.error('提交失败:', error)
    toast.show('提交失败，请稍后重试')
  }
}
</script>